import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Alert, Chip, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Add, Edit, Star, StarBorder, ToggleOn, ToggleOff } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const emptyForm = { code: '', name: '', symbol: '', locale: '', decimalPlaces: 2 };

const CurrencyList = () => {
  const navigate = useNavigate();

  const [currencies, setCurrencies] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');

  const [dlgOpen, setDlgOpen] = useState(false);
  const [form,    setForm]    = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dlgOpen, value: form, onClose: () => setDlgOpen(false) });
  const [editCode, setEditCode] = useState(null);
  const [saving,   setSaving]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/currencies/all`, { headers: hdrs() });
      setCurrencies(data);
    } catch {
      setError('Failed to load currencies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const openAdd = () => {
    setForm(emptyForm);
    setEditCode(null);
    setDlgOpen(true);
  };

  const openEdit = (cur) => {
    setForm({ code: cur.code, name: cur.name, symbol: cur.symbol, locale: cur.locale, decimalPlaces: cur.decimalPlaces });
    setEditCode(cur.code);
    setDlgOpen(true);
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      if (editCode) {
        await axios.put(`${API}/api/currencies/${editCode}`, form, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/currencies`, { ...form, createdBy: JSON.parse(localStorage.getItem('user') || '{}').email || '' }, { headers: hdrs() });
      }
      setSuccess(editCode ? 'Currency updated' : 'Currency added');
      setDlgOpen(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const activate = async (code) => {
    try {
      await axios.put(`${API}/api/currencies/${code}/activate`, {}, { headers: hdrs() });
      setSuccess(`${code} set as local currency`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Activate failed');
    }
  };

  const toggleActive = async (cur) => {
    try {
      await axios.delete(`${API}/api/currencies/${cur.code}`, { headers: hdrs() });
      setSuccess(`${cur.code} ${cur.isActive ? 'deactivated' : 'activated'}`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Toggle failed');
    }
  };

  if (loading) return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading currencies…</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>

          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => navigate('/setups')} sx={{ mr: 1 }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" color="primary">Currencies</Typography>
            </Box>
            <Button startIcon={<Add />} variant="outlined" size="small" onClick={openAdd}>
              Add Currency
            </Button>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Symbol</strong></TableCell>
                <TableCell><strong>Locale</strong></TableCell>
                <TableCell align="center"><strong>Decimals</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currencies.map(cur => (
                <TableRow key={cur.code} hover
                  sx={{ opacity: cur.isActive ? 1 : 0.5 }}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight={700} fontFamily="monospace">{cur.code}</Typography>
                      {cur.isLocal && (
                        <Chip label="Local" size="small" color="success" icon={<Star sx={{ fontSize: '0.85rem !important' }} />} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{cur.name}</TableCell>
                  <TableCell><Typography fontWeight={600}>{cur.symbol}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{cur.locale}</Typography></TableCell>
                  <TableCell align="center">{cur.decimalPlaces}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={cur.isActive ? 'Active' : 'Inactive'}
                      color={cur.isActive ? 'success' : 'default'}
                      size="small" variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(cur)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!cur.isLocal && cur.isActive && (
                      <Tooltip title="Set as Local Currency">
                        <IconButton size="small" color="warning" onClick={() => activate(cur.code)}>
                          <StarBorder fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!cur.isLocal && (
                      <Tooltip title={cur.isActive ? 'Deactivate' : 'Activate'}>
                        <IconButton size="small" color={cur.isActive ? 'error' : 'success'}
                          onClick={() => toggleActive(cur)}>
                          {cur.isActive ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            The <strong>Local</strong> currency is used for all monetary formatting across the system.
            Only one currency can be local at a time.
          </Typography>
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      {discardDialog}
      <Dialog open={dlgOpen} onClose={attemptClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editCode ? `Edit ${editCode}` : 'Add Currency'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={6}>
              <TextField label="ISO Code" value={form.code} size="small" fullWidth
                disabled={Boolean(editCode)}
                onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                helperText="e.g. USD" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Symbol" value={form.symbol} size="small" fullWidth
                onChange={e => setForm(f => ({ ...f, symbol: e.target.value }))}
                helperText="e.g. $" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Name" value={form.name} size="small" fullWidth
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                helperText="e.g. US Dollar" />
            </Grid>
            <Grid item xs={8}>
              <TextField label="Locale (BCP-47)" value={form.locale} size="small" fullWidth
                onChange={e => setForm(f => ({ ...f, locale: e.target.value }))}
                helperText="e.g. en-US" />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Decimals" type="number" value={form.decimalPlaces} size="small" fullWidth
                inputProps={{ min: 0, max: 4 }}
                onChange={e => setForm(f => ({ ...f, decimalPlaces: Number(e.target.value) }))} />
            </Grid>
          </Grid>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CurrencyList;
